import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import { INDIAN_STATES } from '../utils/indianStates';
import { getCitiesByState } from '../utils/indianCities';
import AutocompleteSelect from '../components/AutocompleteSelect';
import { validateIndianMobile, validateIndianPincode } from '../utils/validation';
import ProductCard from '../components/product/ProductCard';
import './Checkout.css';

// Razorpay Key ID (can be overridden via environment variable)
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_SAw3POp3AbAkup';

const Checkout = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bestsellers, setBestsellers] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [expandedStep, setExpandedStep] = useState(1);
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: user?.phone || '',
    shipping_address: '', // Keep for backward compatibility
    shipping_country: 'India',
    shipping_state: 'Tamil Nadu',
    shipping_city: '',
    shipping_pincode: '',
    shipping_landmark: '',
    shipping_street_address: '',
    billing_address: '',
    payment_method: 'razorpay',
  });
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ customer_phone: '', shipping_pincode: '' });
  const [shippingQuote, setShippingQuote] = useState({
    shipping_charge: 0,
    total_weight_kg: 0,
    zone_label: '',
  });
  const [shippingQuoteLoading, setShippingQuoteLoading] = useState(false);
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const orderSummaryRef = useRef(null);
  const lastNotifiedShippingRef = useRef(null);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCart();
    fetchSavedAddress();
    fetchBestsellers();
    loadRazorpay();
  }, [isAuthenticated]);

  // Variant was deleted by admin: no size/variant, or variant_id points to missing variant
  const isItemVariantMissing = (item) => {
    if (item.item_type === 'combo') {
      const details = item.combo_selections_detail || [];
      return details.some((d) => !d.variant);
    }
    if (item.variant_id == null) return true;
    if (item.size == null && item.color_name == null) return true;
    return false;
  };

  // Item is unavailable when inactive or out of stock
  const isItemUnavailable = (item) => {
    if (item.item_type === 'combo') {
      return !item.combo_in_stock;
    }
    if (item.product_unavailable || item.is_active === 0 || item.is_active === false || item.is_active === '0') {
      return true;
    }
    const stockRaw = item.stock_quantity;
    if (stockRaw == null || stockRaw === '') return false;
    const stock = parseInt(stockRaw, 10);
    if (Number.isNaN(stock)) return false;
    const qty = parseInt(item.quantity, 10) || 0;
    if (stock === 0) return true;
    return qty > stock;
  };

  const getUnavailableReason = (item) => {
    if (item.item_type === 'combo') {
      return item.combo_unavailable_reason || 'Out of stock';
    }
    if (item.product_unavailable || item.is_active === 0 || item.is_active === false || item.is_active === '0') {
      return item.product_unavailable_reason || 'Product not available at the moment';
    }
    return 'Out of stock';
  };

  const variantMissingItems = cartItems.filter(isItemVariantMissing);
  const unavailableItems = cartItems.filter(isItemUnavailable);
  const comboOnlyItems = cartItems.filter((item) => item.combo_only);
  const hasVariantMissingItems = variantMissingItems.length > 0;
  const hasUnavailableItems = unavailableItems.length > 0;
  const hasComboOnlyItems = comboOnlyItems.length > 0;
  const hasBlockingCartIssues = hasVariantMissingItems || hasUnavailableItems;

  const loadRazorpay = () => {
    if (window.Razorpay) {
      setRazorpayLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => {
      setRazorpayLoaded(true);
    };
    script.onerror = () => {
      toast.error('Failed to load Razorpay SDK');
    };
    document.body.appendChild(script);
  };

  const fetchSavedAddress = async () => {
    try {
      const response = await api.get('/user/shipping-addresses/default');
      if (response.address) {
        setFormData(prev => ({
          ...prev,
          customer_name: response.address.name || prev.customer_name,
          customer_email: response.address.email || prev.customer_email,
          customer_phone: response.address.phone || prev.customer_phone,
          shipping_address: response.address.address || prev.shipping_address,
          shipping_country: response.address.country || 'India',
          shipping_state: response.address.state || 'Tamil Nadu',
          shipping_city: response.address.city || '',
          shipping_pincode: response.address.pincode || '',
          shipping_landmark: response.address.landmark || '',
          shipping_street_address: response.address.street_address || '',
        }));
      }
    } catch (error) {
      // Ignore if no saved address
    }
  };

  const fetchBestsellers = async () => {
    try {
      const response = await api.get('/products/bestsellers');
      setBestsellers(response.products || []);
    } catch (error) {
      console.error('Error fetching bestsellers:', error);
    }
  };

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCartItems(response.cart || []);
      if (response.cart.length === 0) {
        toast.info('Your cart is empty');
        navigate('/cart');
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: '' }));
    let finalValue = value;
    if (name === 'customer_phone') finalValue = value.replace(/\D/g, '').slice(0, 12);
    if (name === 'shipping_pincode') finalValue = value.replace(/\D/g, '').slice(0, 6);
    if (name === 'shipping_state') {
      setFormData(prev => ({ ...prev, [name]: finalValue, shipping_city: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: finalValue }));
    }
  };

  const calculateSubtotal = () => {
    // Use price from items (merchandise only — no product shipping_charges)
    return cartItems.reduce((total, item) => {
      const basePrice = parseFloat(item.price) || 0;
      return total + basePrice * (parseInt(item.quantity) || 0);
    }, 0);
  };

  const calculateShipping = () => {
    // Weight-based delivery from backend quote (not product.shipping_charges)
    return parseFloat(shippingQuote.shipping_charge) || 0;
  };

  const calculateTaxes = () => {
    return cartItems.reduce((total, item) => {
      if (item.item_type === 'combo') return total;
      const basePrice = parseFloat(item.price) || 0;
      const taxGst = parseFloat(item.tax_gst) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return total + (basePrice * taxGst / 100) * quantity;
    }, 0);
  };
  const handleShowMore = () => {
    navigate('/products');
  }
  const calculateTotal = () => {
    return calculateSubtotal() + calculateTaxes() + calculateShipping();
  };

  // Fetch weight-based shipping quote when address or cart changes
  useEffect(() => {
    if (!isAuthenticated || cartItems.length === 0) {
      setShippingQuote({ shipping_charge: 0, total_weight_kg: 0, zone_label: '' });
      return;
    }
    if (!formData.shipping_state || !formData.shipping_city) {
      setShippingQuote({ shipping_charge: 0, total_weight_kg: 0, zone_label: '' });
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setShippingQuoteLoading(true);
        const res = await api.post('/shipping/quote', {
          shipping_state: formData.shipping_state,
          shipping_city: formData.shipping_city,
        });
        if (!cancelled) {
          const charge = parseFloat(res.shipping_charge) || 0;
          setShippingQuote({
            shipping_charge: charge,
            total_weight_kg: res.total_weight_kg || 0,
            zone_label: res.zone_label || '',
          });

          const notifyKey = `${formData.shipping_state}|${formData.shipping_city}|${charge}`;
          if (charge > 0 && lastNotifiedShippingRef.current !== notifyKey) {
            lastNotifiedShippingRef.current = notifyKey;
            toast.info(`Shipping charge of ₹${charge.toFixed(2)} added`, {
              autoClose: 4000,
            });
            setTimeout(() => {
              orderSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 100);
          }
        }
      } catch (err) {
        console.error('Shipping quote error:', err);
        if (!cancelled) {
          setShippingQuote({ shipping_charge: 0, total_weight_kg: 0, zone_label: '' });
        }
      } finally {
        if (!cancelled) setShippingQuoteLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isAuthenticated, cartItems, formData.shipping_state, formData.shipping_city]);

  const handleContinueToDelivery = () => {
    setFieldErrors({ customer_phone: '', shipping_pincode: '' });
    if (!formData.customer_name || !formData.customer_phone || !formData.shipping_street_address ||
        !formData.shipping_city || !formData.shipping_state || !formData.shipping_pincode) {
      toast.error('Please fill in all required shipping address fields');
      return;
    }
    const phoneValidation = validateIndianMobile(formData.customer_phone);
    if (!phoneValidation.valid) {
      setFieldErrors(prev => ({ ...prev, customer_phone: phoneValidation.message }));
      toast.error(phoneValidation.message);
      return;
    }
    const pincodeValidation = validateIndianPincode(formData.shipping_pincode);
    if (!pincodeValidation.valid) {
      setFieldErrors(prev => ({ ...prev, shipping_pincode: pincodeValidation.message }));
      toast.error(pincodeValidation.message);
      return;
    }
    setCurrentStep(2);
    setExpandedStep(2);
    setTimeout(() => {
      step2Ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 100);
  };

  const handleContinueToPayment = () => {
    setCurrentStep(3);
    setExpandedStep(3);
    setTimeout(() => {
      step3Ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 100);
  };

  const toggleStep = (step) => {
    if (step <= currentStep) {
      setExpandedStep(expandedStep === step ? null : step);
      if (expandedStep !== step) {
        const ref = step === 1 ? step1Ref : step === 2 ? step2Ref : step3Ref;
        setTimeout(() => ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 100);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Block checkout if any item has removed variant (no size/variant)
      if (hasVariantMissingItems) {
        toast.error('Some items have a removed size/variant. Please remove them and add again with the right size and color.');
        setSubmitting(false);
        return;
      }
      // Block checkout if any item is unavailable (no stock or qty > stock)
      if (hasComboOnlyItems) {
        toast.error(
          comboOnlyItems[0]?.product_unavailable_reason ||
            'Some items are combo-only and must be purchased as a combo. Please remove them from your cart.'
        );
        setSubmitting(false);
        return;
      }
      if (hasUnavailableItems) {
        toast.error('Some items are unavailable (inactive product, inactive combo, or out of stock). Please remove them and try again.');
        setSubmitting(false);
        return;
      }

      // Run server-side stock & variant validation before opening payment
      try {
        const validateRes = await api.post('/payments/validate-cart');
        if (validateRes.valid !== true) {
          const msg = validateRes.message || 'Cart validation failed.';
          const errors = (validateRes.stockErrors || []).join(' ');
          toast.error(errors || msg);
          setSubmitting(false);
          return;
        }
      } catch (validateErr) {
        const data = validateErr.response?.data;
        const stockErrors = (data?.stockErrors || []).join(' ');
        const message = stockErrors || data?.message || data?.error || 'Cart validation failed. Please update your cart and try again.';
        toast.error(message);
        setSubmitting(false);
        return;
      }

      const totalAmount = calculateTotal();

      // Initiate Razorpay payment (order will be created only after successful payment)
      if (!razorpayLoaded || !window.Razorpay) {
        toast.error('Payment gateway is loading. Please try again in a moment.');
        setSubmitting(false);
        return;
      }

      // Create Razorpay order (amount recalculated on backend from cart weight + zone)
      const razorpayOrderResponse = await api.post('/payments/create-order', {
        amount: totalAmount,
        currency: 'INR',
        shipping_state: formData.shipping_state,
        shipping_city: formData.shipping_city,
      });
      if (razorpayOrderResponse.shipping_charge != null) {
        setShippingQuote((prev) => ({
          ...prev,
          shipping_charge: razorpayOrderResponse.shipping_charge,
          total_weight_kg: razorpayOrderResponse.total_weight_kg || prev.total_weight_kg,
          zone_label: razorpayOrderResponse.zone || prev.zone_label,
        }));
      }

      // Open Razorpay checkout
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: razorpayOrderResponse.amount,
        currency: razorpayOrderResponse.currency,
        name: 'Freakers',
        description: 'Order Payment',
        order_id: razorpayOrderResponse.id,
        handler: async function (paymentResponse) {
          try {
            // Verify payment and create order on backend (order created only if payment succeeds)
            const verifyResponse = await api.post('/payments/verify-payment', {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
              // Include order data
              customer_name: formData.customer_name,
              customer_email: formData.customer_email,
              customer_phone: formData.customer_phone,
              shipping_address: formData.shipping_address,
              billing_address: formData.billing_address,
              shipping_country: formData.shipping_country,
              shipping_state: formData.shipping_state,
              shipping_city: formData.shipping_city,
              shipping_pincode: formData.shipping_pincode,
              shipping_landmark: formData.shipping_landmark,
              shipping_street_address: formData.shipping_street_address,
            });

            if (verifyResponse.success) {
              toast.success('Payment successful! Order placed.');
              // Dispatch cart updated event to refresh navbar count
              window.dispatchEvent(new CustomEvent('cartUpdated'));
              navigate(`/orders/${verifyResponse.orderId}`);
            } else {
              toast.error(verifyResponse.message || 'Payment verification failed');
              setSubmitting(false);
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Payment verification failed. Please try again.';
            toast.error(errorMessage);
            setSubmitting(false);
          }
        },
        prefill: {
          name: formData.customer_name,
          email: formData.customer_email,
          contact: formData.customer_phone,
        },
        theme: {
          color: '#000000',
        },
        modal: {
          ondismiss: function () {
            // User closed the payment modal
            toast.info('Payment cancelled');
            setSubmitting(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', async function (response) {
        console.error('Payment failed:', response);
        // No order created if payment fails
        toast.error(`Payment failed: ${response.error.description || 'Unknown error'}. Please try again.`);
        setSubmitting(false);
      });

      razorpay.open();
    } catch (error) {
      console.error('Payment initiation error:', error);
      const data = error.response?.data;
      const stockErrors = (data?.stockErrors || []).join(' ');
      toast.error(stockErrors || data?.message || data?.error || 'Failed to initiate payment');
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <Container className="checkout-page">
      <h1 className="checkout-title">Checkout</h1>

      {/* Progress Indicator */}
      <div className="checkout-progress">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-circle">1</div>
          <div className="step-label">Shipping Address</div>
        </div>
        <div className={`progress-line ${currentStep > 1 ? 'active' : ''}`}></div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-circle">2</div>
          <div className="step-label">Delivery Information</div>
        </div>
        <div className={`progress-line ${currentStep > 2 ? 'active' : ''}`}></div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
          <div className="step-circle">3</div>
          <div className="step-label">Payment Method</div>
        </div>
      </div>

      {hasVariantMissingItems && (
        <Alert variant="warning" className="checkout-unavailable-alert mb-4">
          <strong>Some items have a removed size/variant.</strong> Please{' '}
          <Link to="/cart" className="alert-link fw-semibold">remove them from your cart</Link>
          {' '}and add again with the right size and color from the product page.
        </Alert>
      )}
      {hasComboOnlyItems && !hasVariantMissingItems && (
        <Alert variant="warning" className="checkout-unavailable-alert mb-4">
          <strong>Some items are combo-only.</strong>{' '}
          {comboOnlyItems.map((item) => item.product_unavailable_reason).filter(Boolean)[0] ||
            'These products cannot be purchased separately.'}{' '}
          Please <Link to="/cart" className="alert-link fw-semibold">remove them from your cart</Link> and purchase the combo instead.
        </Alert>
      )}
      {hasUnavailableItems && !hasVariantMissingItems && !hasComboOnlyItems && (
        <Alert variant="warning" className="checkout-unavailable-alert mb-4">
          <strong>Some items in your cart are unavailable</strong> (out of stock or not available at the moment). Please{' '}
          <Link to="/cart" className="alert-link fw-semibold">remove them from your cart</Link>
          {' '}before placing the order.
        </Alert>
      )}

      <div className="checkout-layout">
        {/* Left Side - Checkout Steps */}
        <div className="checkout-steps">
          {/* Step 1: Shipping Address */}
          <div ref={step1Ref} className={`checkout-step ${expandedStep === 1 ? 'expanded' : ''}`}>
            <button
              type="button"
              className="step-header"
              onClick={() => toggleStep(1)}
            >
              <span className="step-number">1</span>
              <span className="step-title">Shipping Address</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className={expandedStep === 1 ? 'rotated' : ''}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {expandedStep === 1 && (
              <div className="step-content">
                <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleChange}
                    required
                    placeholder="10 digits (6–9)"
                    maxLength={12}
                    className={fieldErrors.customer_phone ? 'border-danger' : ''}
                  />
                  {fieldErrors.customer_phone && (
                    <Form.Text className="text-danger d-block">{fieldErrors.customer_phone}</Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address Line 1 *</Form.Label>
                  <Form.Control
                    type="text"
                    name="shipping_street_address"
                    value={formData.shipping_street_address}
                    onChange={handleChange}
                    required
                    placeholder="House/Flat No., Building Name, Street"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address Line 2 (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="shipping_landmark"
                    value={formData.shipping_landmark}
                    onChange={handleChange}
                    placeholder="Apt 4B"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>State *</Form.Label>
                      <AutocompleteSelect
                        name="shipping_state"
                        value={formData.shipping_state}
                        onChange={handleChange}
                        options={INDIAN_STATES}
                        placeholder="Type or select state..."
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>City *</Form.Label>
                      <AutocompleteSelect
                        name="shipping_city"
                        value={formData.shipping_city}
                        onChange={handleChange}
                        options={formData.shipping_state ? getCitiesByState(formData.shipping_state) : []}
                        placeholder={formData.shipping_state ? 'Type or select city...' : 'Select state first'}
                        disabled={!formData.shipping_state}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
<Form.Group className="mb-3">
                        <Form.Label>Postal Code *</Form.Label>
                        <Form.Control
                          type="text"
                          name="shipping_pincode"
                          value={formData.shipping_pincode}
                          onChange={handleChange}
                          required
                          placeholder="6 digits only"
                          pattern="[0-9]{6}"
                          maxLength={6}
                          inputMode="numeric"
                          className={fieldErrors.shipping_pincode ? 'border-danger' : ''}
                        />
                        {fieldErrors.shipping_pincode && (
                          <Form.Text className="text-danger d-block">{fieldErrors.shipping_pincode}</Form.Text>
                        )}
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Country *</Form.Label>
                  <Form.Control
                    type="text"
                    name="shipping_country"
                    value={formData.shipping_country}
                    onChange={handleChange}
                    required
                    readOnly
                  />
                </Form.Group>

                <Button
                  type="button"
                  className="continue-btn"
                  onClick={handleContinueToDelivery}
                >
                  CONTINUE TO DELIVERY
                </Button>
                </Form>
              </div>
            )}
          </div>

          {/* Step 2: Delivery Information */}
          <div ref={step2Ref} className={`checkout-step ${expandedStep === 2 ? 'expanded' : ''}`}>
            <button
              type="button"
              className="step-header"
              onClick={() => toggleStep(2)}
            >
              <span className="step-number">2</span>
              <span className="step-title">Delivery Information</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className={expandedStep === 2 ? 'rotated' : ''}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {expandedStep === 2 && (
              <div className="step-content">
                <div className="delivery-info">
                  <div className="info-section">
                    <h6>Delivery Time:</h6>
                    <p>Orders within Tamil Nadu will be delivered within 2 working days.</p>
                    <p>Orders outside Tamil Nadu will be delivered within 3 - 4 working days.</p>
                  </div>

                  <div className="info-section">
                    <h6>Order Processing:</h6>
                    <p>All orders are processed and dispatched as quickly as possible.</p>
                    <p>Orders placed on weekends or public holidays will be processed on the next working day.</p>
                  </div>

                  <div className="info-section">
                    <h6>Tracking Information:</h6>
                    <p>Once your order is shipped, your orders Can be tracked in your order details.</p>
                    <p>Customers can track their shipment until delivery.</p>
                  </div>

                  <div className="info-section">
                    <h6>Delays:</h6>
                    <p>Delivery timelines may vary due to:</p>
                    <ul>
                      <li>Courier delays</li>
                      <li>Weather conditions</li>
                      <li>Festive seasons</li>
                      <li>Operational issues beyond our control</li>
                    </ul>
                    <p>We are not liable for delays caused by external factors once the order is shipped.</p>
                  </div>

                  <div className="info-section">
                    <h6>Shipping Address:</h6>
                    <p>Customers must provide accurate and complete delivery details.</p>
                    <p>We are not responsible for orders delivered to incorrect or incomplete addresses provided by the customer.</p>
                  </div>

                  <div className="info-section">
                    <h6>Shipping Charges:</h6>
                    <p>Any applicable shipping charges will be mentioned at checkout before payment</p>
                  </div>
                </div>

                <Button
                  type="button"
                  className="continue-btn"
                  onClick={handleContinueToPayment}
                >
                  CONTINUE TO PAYMENT
                </Button>
              </div>
            )}
          </div>

          {/* Step 3: Payment Method */}
          <div ref={step3Ref} className={`checkout-step ${expandedStep === 3 ? 'expanded' : ''}`}>
            <button
              type="button"
              className="step-header"
              onClick={() => toggleStep(3)}
            >
              <span className="step-number">3</span>
              <span className="step-title">Payment Method</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className={expandedStep === 3 ? 'rotated' : ''}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {expandedStep === 3 && (
              <div className="step-content">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment Method</Form.Label>
                    <Form.Control
                      type="text"
                      value="Pay Online (Razorpay)"
                      disabled
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      Online payment via Razorpay is required to complete your order.
                    </Form.Text>
                  </Form.Group>

                  <Alert variant="success">
                    <strong>Secure Payment:</strong> You will be redirected to Razorpay's secure payment gateway to complete your payment.
                  </Alert>

                  <Button
                    type="submit"
                    className="continue-btn"
                    disabled={submitting || hasBlockingCartIssues}
                  >
                    {hasVariantMissingItems ? 'Remove items with removed variant to continue' : hasUnavailableItems ? 'Remove unavailable items to continue' : submitting ? 'Placing Order...' : 'PLACE ORDER'}
                  </Button>
                </Form>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Order Summary */}
        <div className="order-summary" ref={orderSummaryRef}>
          <h5 className="order-summary-title">Order Summary</h5>
          
          <div className="order-items">
            {cartItems.map((item) => {
              const isCombo = item.item_type === 'combo';
              const basePrice = parseFloat(item.price) || 0;
              const finalPrice = parseFloat(item.final_price) || 0;
              const taxGst = parseFloat(item.tax_gst) || 0;
              const quantity = parseInt(item.quantity) || 1;
              const variantMissing = isItemVariantMissing(item);
              const unavailable = isItemUnavailable(item);
              const showIssue = variantMissing || unavailable;

              // Line price = merchandise + GST only (delivery is order-level by weight)
              const unitFinalPrice = isCombo
                ? basePrice
                : finalPrice > 0
                  ? finalPrice
                  : basePrice + (basePrice * taxGst) / 100;
              const itemTotal = unitFinalPrice * quantity;

              const comboDetails = item.combo_selections_detail || [];
              const variantInfo = [];
              if (item.size) variantInfo.push(item.size);
              if (item.color_name) variantInfo.push(item.color_name);
              const variantString = variantInfo.length > 0 ? ` (${variantInfo.join(', ')})` : '';

              return (
                <div key={item.id} className={`order-item ${isCombo ? 'order-item-combo' : ''} ${showIssue ? 'order-item-unavailable' : ''}`}>
                  <div className="item-image">
                    <img
                      src={item.product_image || '/placeholder.png'}
                      alt={item.product_name}
                    />
                    {isCombo && <span className="order-item-unavailable-badge" style={{ background: '#16a34a' }}>COMBO</span>}
                    {variantMissing && <span className="order-item-unavailable-badge">Variant removed</span>}
                    {unavailable && !variantMissing && <span className="order-item-unavailable-badge">Unavailable</span>}
                  </div>
                  <div className="item-details">
                    <div className="item-name">{item.product_name}</div>
                    {isCombo && (
                      <div className="small text-muted">
                        {item.combo_includes_label ||
                          comboDetails.map((d) => d.product_name).filter(Boolean).join(' + ') ||
                          'Combo'}
                      </div>
                    )}
                    {!isCombo && variantString && (
                      <div className="small text-muted">{variantString}</div>
                    )}
                    {isCombo && comboDetails.length > 0 && (
                      <div className="checkout-combo-includes">
                        {comboDetails.map((d, idx) => (
                          <div key={d.variant_id || idx} className="checkout-combo-include">
                            {d.product_image && <img src={d.product_image} alt={d.product_name} />}
                            <div>
                              <div className="checkout-combo-include-name">{d.product_name}</div>
                              <div className="small text-muted">
                                {[d.color_name, d.size].filter(Boolean).join(' • ')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {variantMissing && (
                      <div className="order-item-unavailable-msg small text-muted">
                        Remove and add again with the right size and color.
                      </div>
                    )}
                    {unavailable && !variantMissing && (
                      <div className="order-item-unavailable-msg small text-muted">
                        {getUnavailableReason(item)}
                      </div>
                    )}
                    <div className="item-quantity">
                      {quantity} X ₹{unitFinalPrice.toFixed(2)}
                    </div>
                  </div>
                  <div className="item-price">₹{itemTotal.toFixed(2)}</div>
                </div>
              );
            })}
          </div>

          <div className="order-breakdown">
            <div className="breakdown-row">
              <span>Subtotal (Items):</span>
              <span>₹{calculateSubtotal().toFixed(2)}</span>
            </div>
            <div className="breakdown-row">
              <span>GST/Tax:</span>
              <span>₹{calculateTaxes().toFixed(2)}</span>
            </div>
            <div className="breakdown-row">
              <span>Shipping{shippingQuoteLoading ? '…' : ''}:</span>
              <span>₹{calculateShipping().toFixed(2)}</span>
            </div>
            <div className="breakdown-row total">
              <span>Grand Total:</span>
              <span>₹{calculateTotal().toFixed(2)}</span>
            </div>
          </div>

          {/* <Button
            type="button"
            className="proceed-checkout-btn"
            onClick={() => {
              if (currentStep < 3) {
                if (currentStep === 1) handleContinueToDelivery();
                else if (currentStep === 2) handleContinueToPayment();
              } else {
                document.querySelector('form')?.requestSubmit();
              }
            }}
            disabled={submitting}
          >
            {submitting ? 'Processing...' : 'PROCEED TO CHECKOUT'}
          </Button> */}
        </div>
      </div>

     

      {/* Bestsellers Section */}
      {bestsellers.length > 0 && (
        <div className="bestsellers-section mt-5">
          <h2 className="section-title mb-4">Bestsellers</h2>
          <Row>
            {bestsellers.map((product) => (
              <Col key={product.id} md={4} sm={6} xs={6} lg={3} className="mb-4">
                <ProductCard product={product} buttonText="Add to Cart" />
              </Col>
            ))}
          </Row>
           <div className="w-100 flex align-items-center justify-content-center">

      <button onClick={handleShowMore} className="load-more-btn my-4">Show More</button>
      </div>
        </div>
      )}
    </Container>
  );
};

export default Checkout;

